import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { ReactComponent as LogoDM } from "assets/svg/logo-Decision-Manager.svg";
import styled, { createGlobalStyle } from "styled-components";

import { ReactComponent as LogoAT } from "assets/svg/logoAT.svg";

import { Col, Row } from "antd";
import { useProject } from "../../../../utils/utility";
import { PROJECT } from "../../../../utils/constants";

const PrintComparison = forwardRef((props, ref) => {
  useImperativeHandle(ref, () => ({
    printComparison: () => {
      // Set A4 paper size for printing
      const mediaQueryList = window.matchMedia("print");
      mediaQueryList.mediaText = "print and (size: A4)";
      window.print(); // Trigger printing when the function is called
    },
  }));

  const [currentGroup, setCurrentGroup] = useState([]);
  const [titleValsArray, setTitleValsArray] = useState([]);
  const [finalRes, setFinalRes] = useState();
  let pageCount = currentGroup?.length;

  const { project } = useProject();

  function processOptions(options, startIndex = 0, accumulatedOptions = []) {
    if (startIndex >= options.length) {
      return accumulatedOptions;
    }

    const endIndex = Math.min(startIndex + 3, options.length);
    const currentGroup = options?.slice(startIndex, endIndex);

    return processOptions(options, endIndex, [
      ...accumulatedOptions,
      currentGroup,
    ]);
  }

  useEffect(() => {
    if (!finalRes) return;
    const processedOptions = processOptions(finalRes);
    setCurrentGroup(processedOptions);
  }, [finalRes]);

  const [activeCriterias, setActiveCriterias] = useState();

  useEffect(() => {
    setActiveCriterias(props?.activeCriterias);
  }, [props?.activeCriterias]); // Update when props change

  function getSelectValue(optionCriteria, isShortList) {
    if (optionCriteria?.second_stage_is_na && isShortList) {
      return "N/A";
    }
    if (optionCriteria?.second_stage_is_blank && isShortList) {
      return "";
    }
    if (optionCriteria?.is_na && !isShortList) {
      return "N/A";
    }
    if (optionCriteria?.is_blank && !isShortList) {
      return "";
    }
  
    if (!isShortList) {
      return Math.round(optionCriteria?.score ?? 0);
    }
  
    return Math.round(optionCriteria?.second_stage_score ?? 0);
  }

  function mapCriteriaForOptions(options) {
    return options.map((option) => {
      const optionDetail = {
        optionId: option.id,
        image: option.image,
        name: option.name,
        adjusted_score: option.adjusted_score,
        weighted_score: option.weighted_score,
      };

      const mappedCriteria = option.option_criteria.map((criteria) => ({
        optionId: criteria.option,
        criteriaId: criteria.criteria,
        score: getSelectValue(criteria, option?.shortlist),
        secondStageScore: parseFloat(criteria.second_stage_score),
      }));

      return {
        optionDetail,
        mappedCriteria,
      };
    });
  }

  useEffect(() => {
    if (!props?.decision?.options) return;

    // Create optionsOrder similar to decisionDetails.js
    const optionsOrder = [...props.decision.options]
      .sort((a, b) => b?.id - a?.id)
      .map((option, i) => ({
        id: option?.id,
        order: i,
      }));

    // Apply the same ordering logic as in decisionDetails.js
    const orderedOptions = [...props.decision.options]
      .map((option) => ({
        ...option,
        order: optionsOrder.find((o) => o?.id === option?.id)?.order,
      }))
      .sort((a, b) => b?.order - a?.order);

    const mappedOptions = mapCriteriaForOptions(orderedOptions);
    setFinalRes(mappedOptions);
  }, [props?.decision?.options]);

  function compareCriteriaWithOptions(options, activeCriterias) {
    const matchingObjects = [];

    options?.forEach((option) => {
      option?.option_criteria?.forEach((criteriaObj) => {
        const matchingCriteria = activeCriterias?.find(
          (criteria) => criteria?.id === criteriaObj?.criteria
        );

        if (matchingCriteria) {
          const mergedObject = {
            ...criteriaObj,
            ...matchingCriteria,
            option: {
              image: option?.image,
              name: option?.name,
              weighted_score: option?.weighted_score,
              adjusted_score: option?.adjusted_score,
              criteriaId: option?.criteria,
              optionId: option?.id,
            },
          };
          matchingObjects?.push(mergedObject);
        }
      });
    });

    return matchingObjects;
  }

  useEffect(() => {
    if (!currentGroup) return;

    const titleValsArray = currentGroup?.map((group) => {
      const result = compareCriteriaWithOptions(group, activeCriterias);
      // const titleVals = result.reduce((map, obj) => {
      //   if (!map.has(obj.title)) {
      //     map.set(obj.title, []);
      //   }
      //   map.get(obj.title).push(obj.score);
      //   return map;
      // }, new Map());
      // return titleVals;
    });
    setTitleValsArray(titleValsArray);
  }, [currentGroup]);

  function formatDate(dateString) {
    if (!dateString) {
      return "N/A"; // or any default value you want to display for invalid or missing dates
    }
    const [year, month, day] = dateString?.split("-");
    return `${month}/${day}/${year}`;
  }

  function getChunkedLength(totalLength, index) {
    const maxChunkSize = 3;
    const chunkIncrement = index * maxChunkSize;

    return Math.min(maxChunkSize + chunkIncrement, totalLength);
  }

  return (
    <div className="print-document">
      <GlobalPrintStyles />
      <div>
        {[...Array(pageCount)]?.map((_, pageIndex) => {
          const titleVals = titleValsArray[pageIndex] || new Map();
          const titleHeader = currentGroup[pageIndex] || new Map();
          const filterMapCreteria = titleHeader?.map((item) => {
            return item?.mappedCriteria;
          });

          return (
            pageIndex < currentGroup.length && (
              <PrintPage key={pageIndex} className={`print-page page-${pageIndex}`}>
                <Wrapper>
                  <Container>
                    <Header>
                      {project === PROJECT.DM
                        ? <LogoDM />
                        : <LogoAT />
                      }

                      <RightSide>
                        <Heading title={props?.decision?.problem || 'Untitled Decision'}>
                          {props?.decision?.problem || 'Untitled Decision'}
                        </Heading>
                        <Date>
                          Deadline:
                          <Span>
                            {formatDate(props?.decision?.deadline)}
                          </Span>{" "}
                        </Date>
                      </RightSide>
                    </Header>
                    <Heading style={{marginTop:"10px"}}>
                      Options{" "}
                      {getChunkedLength(
                        props?.decision?.options?.length,
                        pageIndex
                      )}
                      <LightFont>
                        {" "}
                        of {props?.decision?.options?.length}
                      </LightFont>{" "}
                    </Heading>
                    <HeroHeader>
                      <Cards>
                        {titleHeader?.map(({ optionDetail }, index) => (
                          <Card key={index}>
                            <CardIconWrapper>
                              <CardIcon
                                src={optionDetail?.image || require("./Placeholder.jpg")}
                                alt={optionDetail?.image || "placeholder"}
                              />
                            </CardIconWrapper>

                            <CardText title={optionDetail?.name || ''}>{optionDetail?.name || 'Unnamed Option'}</CardText>
                            <div>
                              <SubText>
                                Adjusted Score:
                                <Span>{Math.ceil(optionDetail?.adjusted_score)}</Span>
                              </SubText>
                              <SubText>
                                Average Score:{" "}
                                <Span>{Math.ceil(optionDetail?.weighted_score)}</Span>
                              </SubText>
                            </div>
                          </Card>
                        ))}
                      </Cards>
                    </HeroHeader>
                    <Table>
                      {activeCriterias?.map((item, index) => (
                        <RowWrapper
                          key={`criteria-${item?.id || index}`}
                          style={{
                            backgroundColor:
                              index % 2 === 0 ? "#f1f1f1" : "#fff",
                          }}
                        >
                          <Row className="criteria-row" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                            <InnerWrapper>
                              <Col span={1}>
                                <Serial>{index + 1}</Serial>
                              </Col>
                              <Col span={5}>
                                <Content title={item?.title || ''}>{item?.title}</Content>
                              </Col>

                              {filterMapCreteria?.map((fil, filIndex) => (
                                <Col span={6} key={`filter-${filIndex}`}>
                                  {fil?.map((crit, i) => (
                                    <Column key={`crit-${i}-${crit?.criteriaId || 'unknown'}`}>
                                      {crit?.criteriaId === item?.id && (
                                        <Circle
                                          style={{
                                            backgroundColor: i % 2 === 0 ? "#e3e3e3" : "#f5f5f5",
                                          }}
                                        >
                                          {crit?.score}
                                        </Circle>
                                      )}
                                    </Column>
                                  ))}
                                </Col>
                              ))}
                              {/* <Col span={6}>
                                <Column>
                                 
                                  <Content>{index + 1}</Content>
                                </Column>
                              </Col>
                              <Col span={6}>
                                <Column>
                                  <Content>{index + 1}</Content>
                                </Column>
                              </Col>
                              <Col span={6}>
                                <Column>
                                  <Content>{index + 1}</Content>
                                </Column>
                              </Col> */}
                              {/* {titleHeader?.map(({ mappedCriteria }, i) => {
                                return mappedCriteria?.map(
                                  (crit, scoreIndex) => (
                                    <Col span={6} key={crit?.criteriaId}>
                                      <Column>
                                        <Circle
                                          style={{
                                            backgroundColor:
                                              scoreIndex % 2 === 0
                                                ? "#e3e3e3"
                                                : "#f5f5f5",
                                          }}
                                        >
                                          {crit?.criteriaId === item?.id
                                            ? crit?.secondStageScore
                                              ? Math?.ceil(
                                                  crit?.secondStageScore
                                                )
                                              : Math?.ceil(crit?.score)
                                            : " N/A"}
                                          {crit?.criteriaId}
                                        </Circle>
                                      </Column>
                                    </Col>
                                  )
                                );
                              })} */}
                            </InnerWrapper>
                          </Row>
                        </RowWrapper>
                      ))}
                    </Table>

                    <Footer>
                      <FooterSpan>
                        © Arootah Follow us @arootahcoach Visit arootah.com
                      </FooterSpan>
                    </Footer>
                  </Container>
                </Wrapper>
              </PrintPage>
            )
          );
        })}
      </div>
    </div>
  );
});

const PrintPage = styled.div`
 /* Base height for print page (A4 minus header/footer) */
  min-height: calc(1000px - 1rem);
  /* Allow content to flow to additional pages when printing */
  overflow: visible;
  page-break-after: auto;
  
  @media print {
    /* Set default margins for all pages when printing */
    @page {
      margin: 0.5in;
      size: A4;
    }
    
    /* Fix for extra space on subsequent pages */
    &.page-0 {
      /* First page - keep normal spacing */
    }
    
    &:not(.page-0) {
      /* Subsequent pages - remove extra space at top */
       box-decoration-break: clone;
    }
  }
`;

// Styled component for the main wrapper of the comparison summary
const Wrapper = styled.div`
  width: 100%;
  margin-top: 20px;
  
  /* Print-specific styles for the wrapper */
  @media print {
    /* Ensure proper spacing between sections */
    margin-top: 40px;
    margin-bottom: 20px;
    
  }
  background-color: #fff;
`;

const Container = styled.div`
  padding: 1rem 1.7rem;
  position: relative;
`;
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #d9d9d9;
  gap: 20px; /* Add space between logo and right side content */
`;
const RightSide = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-end;
  max-width: 65%; /* Prevent complete overlap with logo */
  text-align: right;
`;
const Heading = styled.h2`
  display: block;
  margin: 0;
  padding: 0;
  color: #1271a6;
  font-size: 18px;
  font-family: Inter;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  transform: translateY(-5px);
  text-align: left;
  width: 100%;
  word-wrap: break-word;
  word-break: break-word;
`;
const Date = styled.p`
  color: #525252;
  font-size: 14px;
  font-family: Inter;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  margin: 0;
  padding: 0;
  transform: translateY(-5px);
`;
const Span = styled.span`
  color: #3090f1;
`;
const HeroHeader = styled.div`
  padding-top: 0.5rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;
const LightFont = styled.span`
  font-weight: 400;
  margin-left: 4px;
`;
const Cards = styled.div`
  margin-left: 140px;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  /* flex-wrap: wrap; */
  width: 68%;
  max-width: fit-content;
  /* margin-right: 0.5rem; */
`;
const Card = styled.div`
  width: 167px;
  height: 167px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 0.5rem;
  
  @media print {
    height: auto;
    min-height: 167px;
    margin-bottom: 1rem;
  }
`;
const CardIconWrapper = styled.div`
  width: 42px;
  height: 42px;
  overflow: hidden;
  border-radius: 4px;

  @media print {
    width: 82px;
    height: 82px;
  }
`;

const CardIcon = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;   /* ensures square without distortion */
  border-radius: 4px;
`;
const CardText = styled.p`
  margin: 0;
  padding: 0;
  color: #000;
  font-size: 14px;
  font-family: Inter;
  font-style: normal;
  font-weight: 600;
  line-height: 17px;
  height: 34px;
  width: 140px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media print {
    height: auto;
    max-height: none;
    -webkit-line-clamp: unset;
    overflow: visible;
    white-space: normal;
    word-break: break-word;
    display: block;
  }
`;
const SubText = styled.p`
  margin: 0;
  padding: 0;
  color: #525252;
  font-size: 12px;
  font-family: Inter;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;
const Table = styled.div`
  margin-top: 1rem;
  /* Remove fixed height constraint to show all criteria */
  min-height: 100px;
  /* border: 1px solid green; */
`;
const RowWrapper = styled.div`
  padding: 4px 84px 4px 10px;
  /* background-color: #f1f1f1; */
  
  @media print {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
    display: block;
    position: relative;
  }
`;
const Serial = styled.p`
  margin: 0;
  padding: 0;
  color: #000;
  font-size: 16px;
  font-family: Inter;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
`;
const Content = styled.p`
  margin: 0;
  padding: 0;
  color: #4b545b;
  font-size: 14px;
  font-family: Poppins;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  vertical-align: middle;
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media print {
    max-width: 150px; /* Limit width in print view to prevent overlap */
  }
`;
const Circle = styled.span`
  /* padding: 0.3rem; */
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;

  /* background: #e3e3e3; */
  color: #000;
  text-align: center;
  font-size: 12px;
  font-family: Inter;
  font-style: normal;
  font-weight: 600;
  margin: 0 auto;
  padding: 0;
  @media print {
  display: inline-flex;
  margin: 6px auto;
  break-inside: avoid;
  }
`;
const Footer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1rem;
  width: 100%;
  
  @media print {
    margin-top: 1rem; /* Reduced top margin */
    position: relative;
    page-break-before: avoid !important; /* Prevent page break before footer */
    page-break-inside: avoid !important; /* Keep footer together */
  }
`;
const FooterSpan = styled.span`
  color: #1271a6;
  font-size: 12px;
  font-family: Poppins;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
`;

const InnerWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  
  @media print {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
    display: flex;
  }
`;
const Column = styled.div`
  transform: translateX(40px) !important;
  display: flex;
  justify-content: center;
  flex-direction: row;
  
  @media print {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
    display: inline-flex;
  }
`;
// Global print CSS: pulled out of component render
const GlobalPrintStyles = createGlobalStyle`
  @media print {
    html, body {
      height: auto !important;
      overflow: auto !important;
      }
    .print-document {
    page-break-after: avoid !important;
    }
  }
  `;
// const SpanCircle = styled.span`
//   background-color: red;
//   width: 30px;
//   height: 30px;
//   display: flex;
//   justify-content: center;
//   align-items: center;
//   border-radius: 50%;
//   /* height: 30px; */
// `;
export default PrintComparison;
